/* eslint-disable @typescript-eslint/no-explicit-any */
import { LegacyTheme } from './legacyTheme.types';
import { getNumericValue, getRootFontSize, isWhite } from '../../utils/stringUtils';
import { ServerThemeColorType, ServerThemeType } from '../serverThemeTypes';
import {
  FontSettings,
  AppSettings,
  FeatureFlags,
  GlobalPillComponentPresetsOptions,
  ContentPresets,
  AlignmentOptions,
} from '../types';
import { alpha, PaletteOptions, ThemeOptions, TypeBackground } from '@mui/material';
import { DEFAULT_THEME_OPTIONS, getDefaultThemePalette } from '../defaultTheme';
import { TypographyOptions } from '@mui/material/styles/createTypography';
import { toNumber } from 'lodash';
import { filterShallowUndefinedObjectEntries } from '../../utils/objectUtils';
import { ComponentCSSOverrides } from '../customTheme';
import { ButtonName, DTCComponentName } from '../componentNames';

const CHECKOUT_COMPONENT_LEGACY_THEME_KEYS = [
  { legacyKey: 'membershipButton', contentKey: 'membershipButton', variantKey: 'signedOutDesktop' },
  { legacyKey: 'membershipButton', contentKey: 'membershipButton', variantKey: 'signedInDesktop' },
  { legacyKey: 'membershipButtonMobile', contentKey: 'membershipButton', variantKey: 'signedOutMobile' },
  { legacyKey: 'membershipButtonMobile', contentKey: 'membershipButton', variantKey: 'signedInMobile' },
  { legacyKey: 'bagButton', contentKey: 'cartButton', variantKey: 'signedOutDesktop' },
  { legacyKey: 'bagButton', contentKey: 'cartButton', variantKey: 'signedInDesktop' },
  { legacyKey: 'bagButtonMobile', contentKey: 'cartButton', variantKey: 'signedOutMobile' },
  { legacyKey: 'bagButtonMobile', contentKey: 'cartButton', variantKey: 'signedInMobile' },
  { legacyKey: 'profileButton', contentKey: 'profileButton', variantKey: 'signedOutDesktop' },
  { legacyKey: 'profileButton', contentKey: 'profileButton', variantKey: 'signedInDesktop' },
  { legacyKey: 'profileButtonMobile', contentKey: 'profileButton', variantKey: 'signedOutMobile' },
  { legacyKey: 'profileButtonMobile', contentKey: 'profileButton', variantKey: 'signedInMobile' },
  { legacyKey: 'buyButton', contentKey: 'addToBagButton', variantKey: 'signedOutDesktop' },
  { legacyKey: 'buyButton', contentKey: 'addToBagButton', variantKey: 'signedInDesktop' },
  { legacyKey: 'buyButtonMobile', contentKey: 'addToBagButton', variantKey: 'signedOutMobile' },
  { legacyKey: 'buyButtonMobile', contentKey: 'addToBagButton', variantKey: 'signedInMobile' },
  { legacyKey: 'plpAddToBagButton', contentKey: 'plpAddToBagButton', variantKey: 'signedOutDesktop' },
] as const;

const getObjectIfHasValues = <T extends Record<string, any>>(obj: T | undefined): T | undefined => {
  if (!obj) {
    return undefined;
  }
  return Object.values(obj).every((value) => [undefined, null, ''].includes(value)) ? undefined : obj;
};

const findLegacyThemeValue = (
  legacyThemeValue: Record<string, any> | undefined,
  key: string,
  condition?: (value: any) => boolean,
): any => {
  if (!legacyThemeValue) {
    return undefined;
  }
  // for legacy theme, there may be multiple components that specify the same key with conflicting values. so we return the one with the most occurrences
  const valueMap = new Map<string | number, number>();
  Object.values(legacyThemeValue).forEach((component) => {
    if (typeof component !== 'object' || !component) {
      return;
    }
    const value = component[key as keyof typeof component];
    if (value) {
      valueMap.set(value, (valueMap.get(value) || 0) + 1);
    }
  });
  const sortedValues = Array.from(valueMap.entries()).sort((a, b) => b[1] - a[1]);
  return sortedValues.find(([value]) => (condition ? condition(value) : true))?.[0];
};

const normalizeFontWeight = (value: string | number | undefined): string | number | undefined => {
  if (typeof value === 'string') {
    // if the value is something like "700" we return 700
    const numericValue = toNumber(value);
    if (Number.isFinite(numericValue)) {
      return numericValue;
    }
    // if the value is something like "Bold" we return "bold"
    return value.toLowerCase();
  }
  return value;
};

const getThemeColors = (legacyTheme: LegacyTheme) => {
  // buy button is usually primary color, so we use its font color as the contrast text color for all colors
  // custom theme will override and select a good contrast text color if needed
  const legacyContrastText = legacyTheme.inpageCheckoutComponent?.buyButton?.fontColor;
  const defaultPalette = getDefaultThemePalette();
  let accent: ServerThemeColorType | undefined = undefined;
  const background: Partial<TypeBackground> = {};
  let primary: ServerThemeColorType = {
    contrastText: legacyContrastText,
    main: defaultPalette.primary.main,
  };
  const secondary: ServerThemeColorType = {
    contrastText: legacyContrastText,
    main: defaultPalette.secondary.main,
  };
  const tertiary: ServerThemeColorType = {
    contrastText: legacyTheme.primary ?? legacyContrastText,
    main: defaultPalette.tertiary.main,
  };
  let textColor: string | undefined = undefined;

  const legacyAnimationDotColor = findLegacyThemeValue(legacyTheme.inpageCheckoutComponent, 'animationDotColor');
  if (legacyAnimationDotColor) {
    accent = { main: legacyAnimationDotColor, contrastText: legacyContrastText };
  }

  if (legacyTheme.inpageCheckoutComponent?.buyButton?.backgroundColor) {
    primary = {
      main: legacyTheme.inpageCheckoutComponent.buyButton.backgroundColor,
      contrastText: legacyTheme.inpageCheckoutComponent.buyButton.fontColor,
    };
  }
  if (legacyTheme.componentStyleOverrides) {
    const legacySecondaryColor = findLegacyThemeValue(legacyTheme.componentStyleOverrides, 'secondary');
    if (legacySecondaryColor) {
      secondary.main = legacySecondaryColor;
    }
  }
  if (legacyTheme.secondary) {
    // allow to override the secondary color obtained from the componentStyleOverrides
    secondary.main = legacyTheme.secondary;
  }
  if (legacyTheme.primary) {
    // previously the primary color was used as the text color, there was no distinction between primary and text color
    textColor = legacyTheme.primary;
  }
  if (
    legacyTheme.tertiary &&
    ![primary?.main, secondary?.main, '', '#'].includes(legacyTheme.tertiary?.trim()) &&
    !isWhite(legacyTheme.tertiary) // unlikely that white was intentionally set as the tertiary color. legacy theme has default values which are unused at times.
  ) {
    tertiary.main = legacyTheme.tertiary;
  }
  if (legacyTheme.dtcGetApp?.backgroundColor) {
    background.alternate = legacyTheme.dtcGetApp.backgroundColor;
  }
  if (legacyTheme.componentStyleOverrides?.dialog?.backgroundColor?.trim()) {
    background.paper = legacyTheme.componentStyleOverrides.dialog.backgroundColor;
    background.default = background.paper;
  }

  return {
    accent: getObjectIfHasValues(accent) ?? defaultPalette.accent,
    background: getObjectIfHasValues(background) ?? defaultPalette.background,
    primary: getObjectIfHasValues(primary) ?? defaultPalette.primary,
    secondary: getObjectIfHasValues(secondary) ?? defaultPalette.secondary,
    tertiary: getObjectIfHasValues(tertiary) ?? defaultPalette.tertiary,
    default: {
      main: textColor ?? defaultPalette.default.main,
    },
    textColor,
  };
};
const getThemeSmartBannerType = (legacyTheme: LegacyTheme): AppSettings['smartBannerType'] => {
  const androidSmartBannerEnabled = !!legacyTheme.smartAppBanner?.androidPopup;
  const iosSmartBannerEnabled =
    !!legacyTheme.smartAppBanner?.iosSmartBanner || !!legacyTheme.getAppBanner?.iosSmartBanner;
  if (androidSmartBannerEnabled && iosSmartBannerEnabled) {
    return 'both';
  }
  if (androidSmartBannerEnabled) {
    return 'android';
  }
  if (iosSmartBannerEnabled) {
    return 'ios';
  }
  return 'none';
};

const getThemeAppSettings = (legacyTheme: LegacyTheme) => {
  const themeAppSettings: AppSettings = {
    smartBannerType: getThemeSmartBannerType(legacyTheme),
  };
  if (legacyTheme.dtcGetApp) {
    themeAppSettings.dtcGetAppImage = legacyTheme.dtcGetApp?.appImage || undefined;
  }
  if (legacyTheme.getAppBanner) {
    themeAppSettings.iosCustomBannerAlignment = {
      align: legacyTheme.getAppBanner.iosCustomBanner?.align as AlignmentOptions['align'],
      alignOffset: legacyTheme.getAppBanner.iosCustomBanner?.alignOffset,
    };
    themeAppSettings.androidCustomBannerAlignment = {
      align: legacyTheme.getAppBanner.androidCustomBanner?.align as AlignmentOptions['align'],
      alignOffset: legacyTheme.getAppBanner.androidCustomBanner?.alignOffset,
    };
  }
  return themeAppSettings;
};

const getThemeFeatureFlags = (legacyTheme: LegacyTheme): FeatureFlags => {
  return {
    bagAnimationDot: legacyTheme.inpageCheckoutComponent?.bagButton?.animationDot,
    scrollSliderDisplay: legacyTheme.inpageCheckoutComponent?.buyButton?.enableScrollSliderDisplay,
    storeLocatorMap: legacyTheme.inpageCheckoutComponent?.buyButton?.enableLocationMap,
    cartCountNumber: legacyTheme.inpageCheckoutComponent?.bagButton?.countNumber,
    bagAnimationDotMobile: legacyTheme.inpageCheckoutComponent?.bagButtonMobile?.animationDot,
    scrollSliderDisplayMobile: legacyTheme.inpageCheckoutComponent?.buyButtonMobile?.enableScrollSliderDisplay,
    storeLocatorMapMobile: legacyTheme.inpageCheckoutComponent?.buyButtonMobile?.enableLocationMap,
    cartCountNumberMobile: legacyTheme.inpageCheckoutComponent?.bagButtonMobile?.countNumber,
    scrollSliderDisplayDistance: getNumericValue(
      legacyTheme.inpageCheckoutComponent?.buyButton?.scrollSliderDisplayDistance,
    ),
    scrollSliderDisplayDistanceMobile: getNumericValue(
      legacyTheme.inpageCheckoutComponent?.buyButtonMobile?.scrollSliderDisplayDistance,
    ),
    showBuyBoxOnNonEligibleClick: legacyTheme.inpageCheckoutComponent?.plpAddToBagButton?.showBuyBoxOnNonEligibleClick,
  };
};

const getThemePillPresets = (legacyTheme: LegacyTheme) => {
  const globalComponentPillPresets: GlobalPillComponentPresetsOptions = {};
  const legacyPillStyles = legacyTheme.customRewardPillColor;
  if (legacyPillStyles) {
    globalComponentPillPresets.default = getObjectIfHasValues({
      main: legacyPillStyles.default,
      contrastText: legacyPillStyles.defaultText,
    });
    globalComponentPillPresets.outOfStock = getObjectIfHasValues({
      main: legacyPillStyles.outOfStock,
      contrastText: legacyPillStyles.outOfStockText,
    });
    globalComponentPillPresets.purchase = getObjectIfHasValues({
      main: legacyPillStyles.purchase,
      contrastText: legacyPillStyles.purchaseText,
    });
    globalComponentPillPresets.survey = getObjectIfHasValues({
      main: legacyPillStyles.survey,
      contrastText: legacyPillStyles.surveyText,
    });
    globalComponentPillPresets.content = getObjectIfHasValues({
      main: legacyPillStyles.content,
      contrastText: legacyPillStyles.contentText,
    });
    globalComponentPillPresets.affiliate = getObjectIfHasValues({
      main: legacyPillStyles.affiliate,
      contrastText: legacyPillStyles.affiliateText,
    });
    globalComponentPillPresets.dailyDeal = getObjectIfHasValues({
      main: legacyPillStyles.dailyDeal,
      contrastText: legacyPillStyles.dailyDealText,
    });
  }
  return getObjectIfHasValues(globalComponentPillPresets);
};

const getThemeCheckoutDrawerPresets = (legacyTheme: LegacyTheme) => {
  const result = {
    customHeaderName: legacyTheme?.checkoutDrawer?.customHeaderName,
    customEmptyBagMessageCardHeaderText: legacyTheme?.componentStyleOverrides?.emptyBagMessageCard?.customHeaderText,
    customCheckoutText: legacyTheme?.checkoutDrawer?.customCheckoutText,
    customViewBagText: legacyTheme?.checkoutDrawer?.customViewBagText,
    checkoutDrawerHeaderButton: legacyTheme?.globalComponentPresets?.checkoutDrawer?.checkoutDrawerHeaderButton,
  };

  // Return undefined if all fields are empty/undefined
  const hasValue = Object.values(result).some((v) => v !== undefined && v !== null && `${v}`.trim() !== '');
  return hasValue ? result : undefined;
};

const getThemeComponentPresets = (legacyTheme: LegacyTheme): ThemeOptions['components'] => {
  const iconSize = findLegacyThemeValue(
    {
      membershipButton: legacyTheme.inpageCheckoutComponent?.membershipButton,
      bagButton: legacyTheme.inpageCheckoutComponent?.bagButton,
      profileButton: legacyTheme.inpageCheckoutComponent?.profileButton,
      buyButton: legacyTheme.inpageCheckoutComponent?.buyButton,
    },
    'size',
  );
  const borderWidth = getNumericValue(legacyTheme.inpageCheckoutComponent?.buyButton?.borderWidth);
  const componentOptions = {
    BCSolidButton: {
      styleOverrides: {
        root: {
          borderRadius: legacyTheme.globalButtonRadius,
        },
      },
    },
    BCOutlineButton: {
      styleOverrides: {
        root: {
          borderRadius: legacyTheme.globalButtonRadius,
          // ensure a borderWidth of 0 gets omitted. This is because 0 was the default empty value for borderWidth in legacy theme
          ...(borderWidth && { borderWidth }),
        },
      },
    },
    BCTextButton: {
      styleOverrides: {
        root: {
          borderRadius: legacyTheme.globalButtonRadius,
        },
      },
    },
    BCIconButton: {
      defaultProps: {
        height: getNumericValue(iconSize),
      },
      styleOverrides: {
        root: {
          borderRadius: legacyTheme.globalButtonRadius,
        },
      },
    },
  } satisfies ThemeOptions['components'];

  return componentOptions;
};

const getThemeContentPresets = (legacyTheme: LegacyTheme, isShopDomain: boolean) => {
  const themeContent: ContentPresets = {};

  // When a theme is saved via the portal it may contain server-side content fields
  // (e.g. buyBoxDialog) alongside legacy fields. Preserve them so they aren't dropped
  // during legacy re-processing.
  const savedContent = (legacyTheme as any).content as ContentPresets | undefined;
  if (savedContent?.buyBoxDialog) {
    themeContent.buyBoxDialog = savedContent.buyBoxDialog;
  }

  if (legacyTheme.customConnectReward?.headerText) {
    themeContent.connectRewardHeader = {
      signedOutDesktop: legacyTheme.customConnectReward.headerText,
      signedInDesktop: legacyTheme.customConnectReward.headerText,
      signedOutMobile: legacyTheme.customConnectReward.headerText,
      signedInMobile: legacyTheme.customConnectReward.headerText,
    };
  }
  if (legacyTheme.inpageCheckoutComponent) {
    // extract content from legacy inpage checkout components
    CHECKOUT_COMPONENT_LEGACY_THEME_KEYS.forEach(({ legacyKey, contentKey, variantKey }) => {
      const legacyComponent = legacyTheme.inpageCheckoutComponent?.[legacyKey];
      if (legacyComponent) {
        const isTextButton = 'textButton' in legacyComponent ? legacyComponent.textButton : !!legacyComponent.text;
        const dtcImgUrl = 'dtcImageUrl' in legacyComponent ? legacyComponent.dtcImageUrl : undefined;
        const imgUrl = 'imageUrl' in legacyComponent ? legacyComponent.imageUrl : undefined;
        const customIconStartSrc = (isShopDomain && dtcImgUrl) || imgUrl;
        themeContent[contentKey] = {
          ...themeContent[contentKey],
          [variantKey]: {
            text: isTextButton ? legacyComponent.text : undefined,
            customIconStartSrc: !isTextButton ? customIconStartSrc : undefined,
            iconLabel: !isTextButton && 'iconLabel' in legacyComponent ? legacyComponent.iconLabel : undefined,
            ...(contentKey === 'plpAddToBagButton' && {
              text: (legacyComponent as any)?.buttonTextOverwrite,
              hideBottomLine: (legacyComponent as any)?.hideBottomLine,
            }),
          },
        };
      }
    });
  }
  return themeContent;
};

const resolveLegacyFont = <K extends keyof Required<LegacyTheme>['barFonts']>(legacyTheme: LegacyTheme, key: K) => {
  // for checkout bar themes, we should prioritize the barFonts, for all other themes we should prioritize the dtcFonts and only use the other as a fallback
  return legacyTheme.activeInpageCheckout
    ? legacyTheme.dtcFonts?.[key] ?? legacyTheme.barFonts?.[key]
    : legacyTheme.barFonts?.[key] ?? legacyTheme.dtcFonts?.[key];
};

const getThemeTypography = (legacyTheme: LegacyTheme) => {
  // THEME FONT SETTINGS
  const themeFontSettings: FontSettings = {};

  themeFontSettings.numericFontOffsetY = toNumber(
    findLegacyThemeValue(legacyTheme.inpageCheckoutComponent, 'countNumberYOffset'),
  );
  if (isNaN(themeFontSettings.numericFontOffsetY)) {
    themeFontSettings.numericFontOffsetY = undefined;
  }
  const fontWeight = normalizeFontWeight(
    legacyTheme.font?.fontWeight ?? legacyTheme.dtcFonts?.fontWeight ?? legacyTheme.barFonts?.fontWeight,
  );

  const fontFamilyLink = resolveLegacyFont(legacyTheme, 'fontFamilyLink');
  const assignedFonts = resolveLegacyFont(legacyTheme, 'assignedFonts');
  const viewBagTextOffsetY = resolveLegacyFont(legacyTheme, 'viewBagTextOffsetY');
  themeFontSettings.fontFamilyLink = fontFamilyLink;
  themeFontSettings.assignedFonts = assignedFonts;
  themeFontSettings.fontWeight = fontWeight;
  themeFontSettings.fontOffsetY = viewBagTextOffsetY;

  // THEME TYPOGRAPHY
  const baseFontSize = resolveLegacyFont(legacyTheme, 'baseFontSize');
  const fontFamily = resolveLegacyFont(legacyTheme, 'fontFamily');

  const btnFontWeight = normalizeFontWeight(legacyTheme.inpageCheckoutComponent?.buyButton?.fontWeight);
  const lgButtonFontSize = getNumericValue(legacyTheme.inpageCheckoutComponent?.buyButton?.fontSize);
  const DEFAULT_BROWSER_FONT_SIZE = 16;
  const htmlFontSize = getRootFontSize() ?? DEFAULT_BROWSER_FONT_SIZE;

  const typography = {
    fontFamily,
    fontSize: baseFontSize,
    htmlFontSize,
    xsButton: {
      ...DEFAULT_THEME_OPTIONS.typography.xsButton,
      fontWeight: btnFontWeight,
      fontFamily: fontFamily ?? 'inherit',
    },
    smButton: {
      ...DEFAULT_THEME_OPTIONS.typography.smButton,
      fontWeight: btnFontWeight,
      fontFamily: fontFamily ?? 'inherit',
    },
    button: {
      ...DEFAULT_THEME_OPTIONS.typography.button,
      fontWeight: btnFontWeight,
      fontFamily: fontFamily ?? 'inherit',
    },
    lgButton: {
      ...DEFAULT_THEME_OPTIONS.typography.lgButton,
      ...(!!lgButtonFontSize && { fontSize: `${lgButtonFontSize}px` }),
      fontWeight: btnFontWeight,
      fontFamily: fontFamily ?? 'inherit',
    },
  } satisfies TypographyOptions;
  return { fontSettings: themeFontSettings, typography };
};

const getThemeComponentCSSOverrides = (legacyTheme: LegacyTheme) => {
  const buyButtonMobileTheme = legacyTheme.inpageCheckoutComponent?.buyButtonMobile;
  const buyButtonTheme = legacyTheme.inpageCheckoutComponent?.buyButton;
  const bagButton = legacyTheme.inpageCheckoutComponent?.bagButton;
  const bagButtonMobile = legacyTheme.inpageCheckoutComponent?.bagButtonMobile;
  // the checkout bar refers to the bag button as cart button
  const checkoutBarBagButton = legacyTheme.componentStyleOverrides?.cartButton;
  const membershipButton = legacyTheme.inpageCheckoutComponent?.membershipButton;
  const membershipButtonMobile = legacyTheme.inpageCheckoutComponent?.membershipButtonMobile;
  const profileButton = legacyTheme.inpageCheckoutComponent?.profileButton;
  const profileButtonMobile = legacyTheme.inpageCheckoutComponent?.profileButtonMobile;
  const locationMapSearchResultsButton = legacyTheme.componentStyleOverrides?.locationMapSearchResultsButton;
  const checkoutQuantityControl = legacyTheme.componentStyleOverrides?.checkoutQuantityControl;
  const emptyBagMessageCard = legacyTheme.componentStyleOverrides?.emptyBagMessageCard;
  const emptyBagMessageCardButton = legacyTheme.componentStyleOverrides?.emptyBagMessageCardButton;

  const outlineStyle =
    legacyTheme.activeInpageCheckout === false && !!checkoutBarBagButton
      ? {
          outlineColor: checkoutBarBagButton.tertiary,
        }
      : undefined;

  // Helper to check if all values in an object are empty
  const isEmptyObject = (obj: Record<string, any>): boolean => {
    return Object.values(obj).every((value) => value === undefined || value === null || value === '');
  };

  // Helper to add entry only if it has non-empty values
  const addIfNotEmpty = (
    target: ComponentCSSOverrides,
    key: string,
    breakpoints: { xs?: Record<string, any>; md?: Record<string, any> },
  ): void => {
    const validBreakpoints: any = {};

    if (breakpoints.xs && !isEmptyObject(breakpoints.xs)) {
      validBreakpoints.xs = breakpoints.xs;
    }

    if (breakpoints.md && !isEmptyObject(breakpoints.md)) {
      validBreakpoints.md = breakpoints.md;
    }

    if (Object.keys(validBreakpoints).length > 0) {
      target[key] = validBreakpoints;
    }
  };

  const componentCSSOverrides: ComponentCSSOverrides = {};

  addIfNotEmpty(componentCSSOverrides, DTCComponentName.EmptyBagMessageCard, {
    xs: {
      backgroundColor: emptyBagMessageCard?.backgroundColor || undefined,
      color: emptyBagMessageCard?.textColor || undefined,
    },
  });

  addIfNotEmpty(componentCSSOverrides, ButtonName.AddToBagButton, {
    xs: {
      color: buyButtonMobileTheme?.fontColor ?? undefined,
      height: buyButtonMobileTheme?.buttonHeight ?? undefined,
      width: buyButtonMobileTheme?.fullWidth ? '100%' : undefined,
      borderRadius: buyButtonMobileTheme?.borderRadius ?? undefined,
      fontSize: buyButtonMobileTheme?.fontSize ? getNumericValue(buyButtonMobileTheme?.fontSize) : undefined,
    },
    md: {
      color: buyButtonTheme?.fontColor ?? undefined,
      height: buyButtonTheme?.buttonHeight ?? undefined,
      width: buyButtonTheme?.fullWidth ? '100%' : undefined,
      borderRadius: buyButtonTheme?.borderRadius ?? undefined,
      fontSize: buyButtonTheme?.fontSize ? getNumericValue(buyButtonTheme?.fontSize) : undefined,
    },
  });
  addIfNotEmpty(componentCSSOverrides, ButtonName.BagDrawerCheckoutButton, {
    xs: {
      borderRadius: legacyTheme.componentStyleOverrides?.bagDrawerCheckoutButton?.globalButtonRadius ?? undefined,
    },
  });
  addIfNotEmpty(componentCSSOverrides, ButtonName.BagDrawerViewBagButton, {
    xs: {
      borderRadius: legacyTheme.componentStyleOverrides?.bagDrawerViewBagButton?.globalButtonRadius ?? undefined,
    },
  });
  addIfNotEmpty(componentCSSOverrides, ButtonName.BagButton, {
    xs: {
      ...outlineStyle,
      color: bagButtonMobile?.textFontColor ?? undefined,
      ...(bagButtonMobile?.textButton
        ? {
            // text button
            fontSize: getNumericValue(bagButtonMobile?.textFontSize),
          }
        : {
            // icon button
            height: getNumericValue(bagButtonMobile?.size),
            width: getNumericValue(bagButtonMobile?.size),
            fontSize: getNumericValue(bagButtonMobile?.countNumberSize) ?? undefined,
          }),
    },
    md: {
      color: bagButton?.textFontColor ?? undefined,
      ...(bagButton?.textButton
        ? {
            // text button
            fontSize: getNumericValue(bagButton?.textFontSize),
          }
        : {
            // icon button
            height: getNumericValue(bagButton?.size),
            width: getNumericValue(bagButton?.size),
            fontSize: getNumericValue(bagButton?.countNumberSize) ?? undefined,
          }),
    },
  });
  addIfNotEmpty(componentCSSOverrides, ButtonName.EmptyBagMessageCardButton, {
    xs: {
      color: emptyBagMessageCardButton?.textColor || undefined,
      backgroundColor: emptyBagMessageCardButton?.backgroundColor || undefined,
    },
  });
  addIfNotEmpty(componentCSSOverrides, ButtonName.CheckoutQuantityControl, {
    xs: {
      backgroundColor: checkoutQuantityControl?.backgroundColor || undefined,
      color: checkoutQuantityControl?.textColor || undefined,
    },
  });
  addIfNotEmpty(componentCSSOverrides, ButtonName.MembershipButton, {
    xs: {
      color: membershipButtonMobile?.textFontColor || undefined,
      ...(membershipButtonMobile?.textButton
        ? {
            // text button
            fontSize: getNumericValue(membershipButtonMobile?.textFontSize),
          }
        : {
            // icon button
            height: getNumericValue(membershipButtonMobile?.size),
            width: getNumericValue(membershipButtonMobile?.size),
          }),
    },
    md: {
      color: membershipButton?.textFontColor || undefined,
      ...(membershipButton?.textButton
        ? {
            // text button
            fontSize: getNumericValue(membershipButton?.textFontSize),
          }
        : {
            // icon button
            height: getNumericValue(membershipButton?.size),
            width: getNumericValue(membershipButton?.size),
          }),
    },
  });

  addIfNotEmpty(componentCSSOverrides, ButtonName.ProfileButton, {
    xs: {
      color: profileButtonMobile?.textFontColor || undefined,
      ...(profileButtonMobile?.textButton
        ? {
            // text button
            fontSize: getNumericValue(profileButtonMobile?.textFontSize),
          }
        : {
            // icon button
            height: getNumericValue(profileButtonMobile?.size),
            width: getNumericValue(profileButtonMobile?.size),
          }),
    },
    md: {
      color: profileButton?.textFontColor || undefined,
      ...(profileButton?.textButton
        ? {
            // text button
            fontSize: getNumericValue(profileButton?.textFontSize),
          }
        : {
            // icon button
            height: getNumericValue(profileButton?.size),
            width: getNumericValue(profileButton?.size),
          }),
    },
  });
  addIfNotEmpty(componentCSSOverrides, ButtonName.LocationMapSearchResultsButton, {
    xs: {
      backgroundColor: locationMapSearchResultsButton?.backgroundColor || undefined,
      color: locationMapSearchResultsButton?.textColor || undefined,
    },
  });

  return componentCSSOverrides;
};

const isLegacyTheme = (legacyTheme: unknown): legacyTheme is LegacyTheme => {
  if (typeof legacyTheme !== 'object' || !legacyTheme) {
    return false;
  }
  return 'activeInpageCheckout' in legacyTheme || 'componentStyleOverrides' in legacyTheme;
};

const isServerTheme = (theme: unknown): theme is ServerThemeType => {
  if (typeof theme !== 'object' || !theme) {
    return false;
  }
  return 'settings' in theme && 'content' in theme;
};

export const parseLegacyDtcThemeResponse = (
  legacyTheme: ServerThemeType | LegacyTheme,
  isShopDomain = false, // this is only to be able to retrieve the correct custom icons for the shop domain
): ServerThemeType | undefined => {
  if (!isLegacyTheme(legacyTheme) && isServerTheme(legacyTheme)) {
    // we have to check both to ensure that we don't return an object that has both legacy and server theme properties
    return legacyTheme;
  }
  if (!isLegacyTheme(legacyTheme)) {
    // we don't want to handle a non-legacy theme
    return undefined;
  }
  const dtcMode = legacyTheme.activeInpageCheckout ? 'inpage' : 'checkout-bar';
  const { fontSettings, typography } = getThemeTypography(legacyTheme);
  const theme: ServerThemeType = {
    settings: {
      app: getThemeAppSettings(legacyTheme),
      featureFlags: getThemeFeatureFlags(legacyTheme),

      fonts: fontSettings,
    },
    dtcMode,
    content: getThemeContentPresets(legacyTheme, isShopDomain),
    globalComponentPresets: {
      pill: getThemePillPresets(legacyTheme),
      checkoutDrawer: getThemeCheckoutDrawerPresets(legacyTheme),
    },
    components: getThemeComponentPresets(legacyTheme),
    componentCSSOverrides: getThemeComponentCSSOverrides(legacyTheme),
    typography,
    ...(legacyTheme.customCss ? { customCss: legacyTheme.customCss } : {}),
    ...getThemeColors(legacyTheme),
  };

  return theme;
};

export const buildThemeFromServerTheme = (
  styleData: Partial<ServerThemeType> | undefined,
  isShopDomain = false,
): ThemeOptions | undefined => {
  if (!styleData) {
    return undefined;
  }
  const parsedTheme = parseLegacyDtcThemeResponse(styleData, isShopDomain);
  if (parsedTheme && typeof parsedTheme === 'object' && 'primary' in parsedTheme) {
    const { primary, secondary, tertiary, accent, background, draftId: _draftId, textColor, ...rest } = parsedTheme;

    const palette: PaletteOptions = {
      primary,
      secondary,
      tertiary,
      accent,
      background,
      text: {
        primary: textColor,
        secondary: textColor && alpha(textColor, 0.7),
      },
      ...(textColor && {
        default: {
          main: textColor,
        },
      }),
    };

    return filterShallowUndefinedObjectEntries({ ...rest, palette });
  }
  // theme already parsed
  return styleData;
};
